import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { PageCTA } from '../components/PageCTA';
const steps = [{
  number: '01',
  title: 'Contexto y enfoque',
  description: 'Recogemos lo esencial: negocio, prioridades, audiencia y material disponible. Definimos qué debe entender una persona al llegar.'
}, {
  number: '02',
  title: 'Estructura y dirección visual',
  description: 'Ordenamos contenidos y construimos una dirección de diseño que haga visible el carácter de tu marca sin sacrificar claridad.'
}, {
  number: '03',
  title: 'Diseño y desarrollo',
  description: 'Convertimos la propuesta en una experiencia responsive, cuidada al detalle y preparada para funcionar en cada pantalla.'
}, {
  number: '04',
  title: 'Revisión compartida',
  description: 'Revisamos contigo lo importante para que el resultado represente correctamente a tu negocio antes de publicar.'
}, {
  number: '05',
  title: 'Publicación y continuidad',
  description: 'Lanzamos el proyecto y dejamos una base clara para acompañar los cambios que necesites después.'
}];
const commitments = [['Una conversación directa', 'Menos capas entre tu necesidad y las decisiones del proyecto.'], ['Un sistema entendible', 'Cada fase tiene un propósito, entregables definidos y una dirección concreta.'], ['Decisiones con criterio', 'Priorizamos lo que ayuda a explicar, orientar y generar confianza.']];
export function ProcessPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-[#0a0a0a] pt-20 sm:pt-24" data-id="element-472" data-mp="2iyuoa8cqqhtu">
      <section className="page-header" data-id="element-473" data-mp="2nu9xuo3ptrkg">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-474" data-mp="2nxe925fllnkm">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.26,
          ease: [0.23, 1, 0.32, 1]
        }} className="grid gap-7 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-475" data-mp="17fqn0gdn0dso">
            <div data-id="element-476" data-mp="jkotqdth3eyd">
              <p className="eyebrow" data-id="element-477" data-mp="jk5amts36kir">Proceso</p>
              <h1 className="mt-5 text-4xl font-medium tracking-[-0.055em] text-white sm:text-6xl" data-id="element-478" data-mp="enevc8o3vsye">
                Una ruta clara para llegar a algo bien resuelto.
              </h1>
            </div>
            <p className="max-w-2xl text-base leading-relaxed text-neutral-300 sm:text-lg" data-id="element-479" data-mp="4ofzjqofvrst">
              Un proceso breve y estructurado evita que el proyecto se convierta
              en una sucesión de decisiones desconectadas.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-shell border-b border-white/10" data-id="element-480" data-mp="1v1evnkqv5km6">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-481" data-mp="1uysm5fkewv1b">
          <ol className="border-l border-white/10" data-id="element-482" data-mp="1q2lk3xv503on">
            {steps.map((step, index) => <motion.li key={step.number} initial={{
            opacity: 0,
            x: -10
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            amount: 0.1
          }} transition={{
            duration: 0.26,
            delay: Math.min(index, 4) * 0.04,
            ease: [0.23, 1, 0.32, 1]
          }} className="relative grid gap-5 border-b border-white/10 py-8 pl-8 last:border-b-0 sm:grid-cols-[110px_1fr] sm:gap-8 sm:pl-12" data-id="element-483" data-mp="1xl1q903w7v0b" data-mpi={index}>
                <span className="absolute -left-[5px] top-10 h-2.5 w-2.5 bg-white" aria-hidden="true" data-id="element-484" data-mp="1snt5crrd1cvd" />
                <p className="meta-number text-[11px]" data-id="element-485" data-mp="340vnvsd0sci6">
                  {step.number}
                </p>
                <div data-id="element-486" data-mp="32lj5czu5zjts">
                  <h2 className="text-xl font-medium tracking-[-0.025em] text-white" data-id="element-487" data-mp="32kzx6aubg9sy">
                    {step.title}
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-300" data-id="element-488" data-mp="3cie5fn635sa6">
                    {step.description}
                  </p>
                </div>
              </motion.li>)}
          </ol>
        </div>
      </section>

      <section className="section-shell border-b border-white/10 bg-black" aria-labelledby="commitments-title" data-id="element-489" data-mp="3lhrggk9uyox0">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-490" data-mp="3v95kn5cr8cwh">
          <p className="eyebrow" data-id="element-491" data-mp="3v8dnvr7f894m">Cómo trabajamos</p>
          <h2 id="commitments-title" className="mt-5 max-w-2xl text-3xl font-medium tracking-[-0.045em] text-white sm:text-4xl" data-id="element-492" data-mp="3q9iz88mrvqcl">
            El proceso también es parte de la experiencia.
          </h2>
          <div className="mt-10 grid overflow-hidden border border-white/10 md:grid-cols-3" data-id="element-493" data-mp="44fb0pyrieys">
            {commitments.map(([title, description]) => <div key={title} className="p-7 border-b border-white/10 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0" data-id="element-494" data-mp="rqraraa0brwn">
                <CheckIcon size={17} className="text-white" aria-hidden="true" data-id="element-495" data-mp="ipjd8ufbev3m" />
                <h3 className="mt-8 text-base font-medium text-white" data-id="element-496" data-mp="3zvbxt74vwfw">
                  {title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-497" data-mp="8soh2i6il6si">
                  {description}
                </p>
              </div>)}
          </div>
        </div>
      </section>
      <PageCTA data-id="element-498" data-mp="3v4vdw2tn8dmg" />
    </div>;
}